// PriceTablesTab.js - Range-based pricing tables (like ref_rangeWindowPricesWhite)
// Rows = square footage ranges, Columns = window types
import {
  React,
  useEffect,
  useState,
  useCallback,
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  DataGrid,
  authFetch,
  authFetchRequest,
  alertC,
  confirmC,
  DIMENSION_LABELS
} from './PricingCommon';

const PriceTablesTab = () => {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTable, setSelectedTable] = useState(null);
  const [openTableDialog, setOpenTableDialog] = useState(false);
  const [editTableData, setEditTableData] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });

  const fetchTables = () => {
    setLoading(true);
    authFetch('/pricing/tables')
      .then(data => { setTables(data); setLoading(false); })
      .catch(err => { console.error('Failed to fetch tables:', err); setLoading(false); });
  };

  useEffect(() => { fetchTables(); }, []);

  const handleSaveTable = async (formData) => {
    try {
      let updatedRow;
      if (editTableData?.id) {
        updatedRow = await authFetchRequest(`/pricing/tables/${editTableData.id}`, formData, 'PUT');
        setTables(prev => prev.map(row => row.id === editTableData.id ? updatedRow : row));
        if (selectedTable?.id === editTableData.id) setSelectedTable(updatedRow);
      } else {
        updatedRow = await authFetchRequest('/pricing/tables', formData, 'POST');
        setTables(prev => [...prev, updatedRow]);
      }
      setOpenTableDialog(false);
      setEditTableData(null);

    } catch (err) {
      console.error('Save failed:', err);
      alertC('Failed to save price table');
    }
  };

  const handleDeleteTable = async () => {
    if (!selectedTable) return;
    const confirmed = await confirmC('Are you sure you want to delete this price table and all its data?');
    if (!confirmed) return;
    try {
      await authFetchRequest(`/pricing/tables/${selectedTable.id}`, null, 'DELETE');
      setTables(prev => prev.filter(row => row.id !== selectedTable.id));
      setSelectedTable(null);

    } catch (err) {
      console.error('Delete failed:', err);
      alertC('Failed to delete price table');
    }
  };

  const tableColumns = [
    { field: 'id', headerName: 'ID', width: 60 },
    { field: 'code', headerName: 'Code', width: 200 },
    { field: 'name', headerName: 'Name', width: 220 },
    { field: 'dimension_basis', headerName: 'Dimension', width: 120, renderCell: (params) => DIMENSION_LABELS[params.value] || params.value },
    { field: 'active', headerName: 'Active', width: 80, renderCell: (params) => params.value ? 'Yes' : 'No' }
  ];

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>Price Tables</Typography>

      {/* Tables List Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Price Tables</Typography>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <Button variant="contained" size="small" onClick={() => { setEditTableData(null); setOpenTableDialog(true); }}>
            Add Table
          </Button>
          <Button variant="contained" color="secondary" size="small" onClick={() => { setEditTableData(selectedTable); setOpenTableDialog(true); }} disabled={!selectedTable}>
            Edit Table
          </Button>
          <Button variant="outlined" color="error" size="small" onClick={handleDeleteTable} disabled={!selectedTable}>
            Delete Table
          </Button>
        </Stack>

        <Box sx={{ mb: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <span style={{ fontSize: '0.85em', color: '#555' }}>
            Price tables define pricing based on dimension ranges (rows) and window types (columns).
            Click a table to edit its rows, columns, and cell values.
          </span>
        </Box>

        {loading ? <CircularProgress size={24} /> : (
          <DataGrid
            autoHeight
            rowHeight={30}
            rows={tables}
            columns={tableColumns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 20, 50]}
            onRowClick={(params) => setSelectedTable(params.row)}
            sx={{
              '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#e3f2fd' },
              '& .MuiDataGrid-row': { cursor: 'pointer' }
            }}
            getRowClassName={(params) => params.row.id === selectedTable?.id ? 'Mui-selected' : ''}
          />
        )}
      </Paper>

      {/* Selected Table Editor */}
      {selectedTable && (
        <PriceTableEditor table={selectedTable} />
      )}

      {/* Table Dialog */}
      <PriceTableDialog
        open={openTableDialog}
        data={editTableData}
        onClose={() => { setOpenTableDialog(false); setEditTableData(null); }}
        onSave={handleSaveTable}
      />
    </Box>
  );
};

const PriceTableDialog = ({ open, data, onClose, onSave }) => {
  const [formData, setFormData] = useState({ code: '', name: '', description: '', dimension_basis: 'area', active: true });

  useEffect(() => {
    if (data) {
      setFormData({
        code: data.code || '',
        name: data.name || '',
        description: data.description || '',
        dimension_basis: data.dimension_basis || 'area',
        active: data.active ?? true
      });
    } else {
      setFormData({ code: '', name: '', description: '', dimension_basis: 'area', active: true });
    }
  }, [data, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{data?.id ? 'Edit Price Table' : 'Add Price Table'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Code" value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value })} fullWidth placeholder="e.g., ref_rangeWindowPricesWhite" />
          <TextField label="Name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} fullWidth placeholder="e.g., Window Prices - White" />
          <TextField label="Description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} fullWidth multiline rows={2} />
          <FormControl fullWidth>
            <InputLabel>Dimension Basis</InputLabel>
            <Select value={formData.dimension_basis} onChange={(e) => setFormData({ ...formData, dimension_basis: e.target.value })} label="Dimension Basis">
              <MenuItem value="area">Area (sqft)</MenuItem>
              <MenuItem value="perimeter">Perimeter (ft)</MenuItem>
              <MenuItem value="width">Width (in)</MenuItem>
              <MenuItem value="height">Height (in)</MenuItem>
              <MenuItem value="fixed">Fixed</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel control={<Checkbox checked={formData.active} onChange={(e) => setFormData({ ...formData, active: e.target.checked })} />} label="Active" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(formData)}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

// Full table editor with spreadsheet-like view and CRUD
const PriceTableEditor = ({ table }) => {
  const [loading, setLoading] = useState(true);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [cells, setCells] = useState({});
  const [editingCell, setEditingCell] = useState(null);
  const [cellValue, setCellValue] = useState('');
  const [openColumnDialog, setOpenColumnDialog] = useState(false);
  const [openRowDialog, setOpenRowDialog] = useState(false);
  const [editColumn, setEditColumn] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const tableContainerRef = React.useRef(null);

  const fetchTableData = useCallback((preserveScroll = false) => {
    if (!table?.id) return;
    const scrollTop = preserveScroll && tableContainerRef.current ? tableContainerRef.current.scrollTop : 0;
    const scrollLeft = preserveScroll && tableContainerRef.current ? tableContainerRef.current.scrollLeft : 0;

    if (!preserveScroll) setLoading(true);
    authFetch(`/pricing/tables/${table.id}/full`)
      .then(data => {
        setColumns(data.columns || []);
        setRows(data.rows || []);
        setCells(data.cells || {});
        setLoading(false);
        // Restore scroll position after state update
        if (preserveScroll) {
          setTimeout(() => {
            if (tableContainerRef.current) {
              tableContainerRef.current.scrollTop = scrollTop;
              tableContainerRef.current.scrollLeft = scrollLeft;
            }
          }, 0);
        }
      })
      .catch(err => {
        console.error('Failed to fetch table data:', err);
        setLoading(false);
      });
  }, [table?.id]);

  useEffect(() => { fetchTableData(false); }, [fetchTableData]);

  const getCellData = (rowId, colId) => cells[`${rowId}_${colId}`] || null;

  const getCellDisplay = (rowId, colId) => {
    const cell = getCellData(rowId, colId);
    if (!cell) return '-';
    if (cell.value_type === 'formula') return cell.value_formula || '-';
    return cell.value_numeric != null ? cell.value_numeric : '-';
  };

  // Cell editing
  const handleCellClick = (rowId, colId) => {
    const cell = getCellData(rowId, colId);
    setEditingCell({ rowId, colId, cell });
    setCellValue(cell ? (cell.value_type === 'formula' ? cell.value_formula : String(cell.value_numeric || '')) : '');
  };

  const handleCellSave = async () => {
    if (!editingCell) return;
    const { rowId, colId, cell } = editingCell;
    const isFormula = cellValue.includes('*') || cellValue.includes('/') || cellValue.includes('+');
    const payload = {
      value_type: isFormula ? 'formula' : 'fixed',
      value_numeric: isFormula ? null : parseFloat(cellValue) || 0,
      value_formula: isFormula ? cellValue : null
    };
    try {
      if (cell?.id) {
        await authFetchRequest(`/pricing/tables/${table.id}/rows/${rowId}/cells/${cell.id}`, payload, 'PUT');
      } else {
        await authFetchRequest(`/pricing/tables/${table.id}/rows/${rowId}/cells`, { ...payload, pricing_table_column_id: colId }, 'POST');
      }
      fetchTableData(true);
      setEditingCell(null);

    } catch (err) {
      alertC('Failed to save cell: ' + err.message);
    }
  };

  // Column CRUD
  const handleSaveColumn = async (formData) => {
    try {
      if (editColumn?.id) {
        await authFetchRequest(`/pricing/tables/${table.id}/columns/${editColumn.id}`, formData, 'PUT');
      } else {
        await authFetchRequest(`/pricing/tables/${table.id}/columns`, { ...formData, sort_order: columns.length + 1 }, 'POST');
      }
      fetchTableData(true);
      setOpenColumnDialog(false);
      setEditColumn(null);

    } catch (err) {
      alertC('Failed to save column: ' + err.message);
    }
  };

  const handleDeleteColumn = async (col) => {
    if (!await confirmC(`Delete column "${col.label}"? This will delete all cells in this column.`)) return;
    try {
      await authFetchRequest(`/pricing/tables/${table.id}/columns/${col.id}`, null, 'DELETE');
      fetchTableData(true);

    } catch (err) {
      alertC('Failed to delete column: ' + err.message);
    }
  };

  // Row CRUD
  const handleSaveRow = async (formData) => {
    try {
      if (editRow?.id) {
        await authFetchRequest(`/pricing/tables/${table.id}/rows/${editRow.id}`, formData, 'PUT');
      } else {
        await authFetchRequest(`/pricing/tables/${table.id}/rows`, { ...formData, sort_order: rows.length + 1 }, 'POST');
      }
      fetchTableData(true);
      setOpenRowDialog(false);
      setEditRow(null);

    } catch (err) {
      alertC('Failed to save row: ' + err.message);
    }
  };

  const handleDeleteRow = async (row) => {
    if (!await confirmC(`Delete row "${row.min_value} - ${row.max_value}"? This will delete all cells in this row.`)) return;
    try {
      await authFetchRequest(`/pricing/tables/${table.id}/rows/${row.id}`, null, 'DELETE');
      fetchTableData(true);

    } catch (err) {
      alertC('Failed to delete row: ' + err.message);
    }
  };

  if (loading) return <Paper sx={{ p: 2 }}><CircularProgress size={24} /></Paper>;

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>{table.name} ({table.code})</Typography>

      {/* Toolbar */}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button size="small" variant="outlined" onClick={() => { setEditColumn(null); setOpenColumnDialog(true); }}>+ Column</Button>
        <Button size="small" variant="outlined" onClick={() => { setEditRow(null); setOpenRowDialog(true); }}>+ Row</Button>
      </Stack>

      {/* Table */}
      <Box ref={tableContainerRef} sx={{ overflowX: 'auto', maxHeight: 500, overflowY: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.8rem' }}>
          <thead>
            <tr style={{ backgroundColor: '#e3f2fd' }}>
              <th style={{ border: '1px solid #ccc', padding: '6px 8px', textAlign: 'left', minWidth: 140 }}>Range</th>
              {columns.map(col => (
                <th key={col.id} style={{ border: '1px solid #ccc', padding: '4px 6px', textAlign: 'center', minWidth: 90 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    <span style={{ fontSize: '0.75rem' }}>{col.label || col.code}</span>
                    <Stack direction="row" spacing={0.5}>
                      <Button size="small" sx={{ minWidth: 20, p: 0, fontSize: '0.6rem' }} onClick={() => { setEditColumn(col); setOpenColumnDialog(true); }}>E</Button>
                      <Button size="small" sx={{ minWidth: 20, p: 0, fontSize: '0.6rem', color: 'error.main' }} onClick={() => handleDeleteColumn(col)}>X</Button>
                    </Stack>
                  </Box>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.id}>
                <td style={{ border: '1px solid #ccc', padding: '4px 6px', backgroundColor: '#f5f5f5' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <span style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>{row.min_value} - {row.max_value}</span>
                    <Button size="small" sx={{ minWidth: 18, p: 0, fontSize: '0.55rem' }} onClick={() => { setEditRow(row); setOpenRowDialog(true); }}>E</Button>
                    <Button size="small" sx={{ minWidth: 18, p: 0, fontSize: '0.55rem', color: 'error.main' }} onClick={() => handleDeleteRow(row)}>X</Button>
                  </Box>
                </td>
                {columns.map(col => {
                  const val = getCellDisplay(row.id, col.id);
                  const isFormula = typeof val === 'string' && (val.includes('*') || val.includes('/'));
                  const isEditing = editingCell?.rowId === row.id && editingCell?.colId === col.id;
                  return (
                    <td key={col.id} onClick={() => !isEditing && handleCellClick(row.id, col.id)}
                      style={{ border: '1px solid #ccc', padding: '4px 6px', textAlign: 'right', cursor: 'pointer',
                        backgroundColor: isEditing ? '#bbdefb' : (isFormula ? '#fff8e1' : 'white'),
                        fontFamily: isFormula ? 'monospace' : 'inherit', fontSize: '0.75rem' }}>
                      {isEditing ? (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <input type="text" value={cellValue} onChange={(e) => setCellValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleCellSave(); if (e.key === 'Escape') setEditingCell(null); }}
                            style={{ width: 70, fontSize: '0.7rem', padding: 2 }} autoFocus />
                          <Button size="small" sx={{ minWidth: 22, p: 0, fontSize: '0.55rem' }} onClick={handleCellSave}>OK</Button>
                        </Box>
                      ) : val}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>

      <Box sx={{ mt: 2, p: 1, bgcolor: '#f0f0f0', borderRadius: 1 }}>
        <Typography variant="caption" sx={{ color: '#666' }}>
          {rows.length} rows × {columns.length} columns. Click cell to edit. Yellow = formula (e.g. 9.80*square)
        </Typography>
      </Box>

      {/* Column Dialog */}
      <Dialog open={openColumnDialog} onClose={() => setOpenColumnDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editColumn?.id ? 'Edit Column' : 'Add Column'}</DialogTitle>
        <DialogContent>
          <PriceTableColumnForm data={editColumn} onSave={handleSaveColumn} onCancel={() => setOpenColumnDialog(false)} />
        </DialogContent>
      </Dialog>

      {/* Row Dialog */}
      <Dialog open={openRowDialog} onClose={() => setOpenRowDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editRow?.id ? 'Edit Row' : 'Add Row'}</DialogTitle>
        <DialogContent>
          <PriceTableRowForm data={editRow} onSave={handleSaveRow} onCancel={() => setOpenRowDialog(false)} />
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

const PriceTableColumnForm = ({ data, onSave, onCancel }) => {
  const [formData, setFormData] = useState({ code: '', label: '' });
  useEffect(() => { setFormData({ code: data?.code || '', label: data?.label || '' }); }, [data]);
  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <TextField label="Code" value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value })} fullWidth size="small" placeholder="e.g. Picture" />
      <TextField label="Label" value={formData.label} onChange={(e) => setFormData({ ...formData, label: e.target.value })} fullWidth size="small" placeholder="e.g. Picture Window" />
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(formData)}>Save</Button>
      </Stack>
    </Stack>
  );
};

const PriceTableRowForm = ({ data, onSave, onCancel }) => {
  const [formData, setFormData] = useState({ min_value: 0, max_value: 0 });
  useEffect(() => { setFormData({ min_value: data?.min_value || 0, max_value: data?.max_value || 0 }); }, [data]);
  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <TextField label="Min Value" type="number" value={formData.min_value} onChange={(e) => setFormData({ ...formData, min_value: parseFloat(e.target.value) })} fullWidth size="small" inputProps={{ step: 0.001 }} />
      <TextField label="Max Value" type="number" value={formData.max_value} onChange={(e) => setFormData({ ...formData, max_value: parseFloat(e.target.value) })} fullWidth size="small" inputProps={{ step: 0.001 }} />
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(formData)}>Save</Button>
      </Stack>
    </Stack>
  );
};

export default PriceTablesTab;
